import type { SerializedCombinedMeter } from '@enmon/commons/lib/enmon';
import type { InventorizationSyncPayload } from '@/features/sync/engine/inventorization-sync-payload';
import { normalizeSearchField } from '@/shared/meter-identification';

/**
 * Resolves the meter a payload refers to, preferring `_id`, then `id`, then the QR/API key.
 * Null means conflicting identities; an empty result means a new meter. A known `_id` missing from the
 * location listing is passed through so the server decides; a bare `id` without `_id` cannot be updated.
 */
export function resolveInventorizationMeterIds(
  payload: InventorizationSyncPayload,
  meters: SerializedCombinedMeter[],
): Pick<InventorizationSyncPayload, '_id' | 'id'> | null {
  const apiKey = normalizeSearchField(payload.identity.apiKey);
  const serial = normalizeSearchField(payload.identity.serial);
  const commodity = payload.identity.commodity;
  const hasExplicitIds = Boolean(payload._id || payload.id);
  const apiKeyMatches = apiKey ? meters.filter(item => normalizeSearchField(item.apiKey) === apiKey) : [];

  const explicitMatch =
    (payload._id ? meters.find(item => item._id === payload._id) : undefined) ??
    (payload.id ? meters.find(item => Number(item.toolsId) === payload.id) : undefined);
  if (!explicitMatch && apiKeyMatches.length > 1) return null;
  const meter = explicitMatch ?? apiKeyMatches[0];

  if (!meter) {
    if (payload._id) return { _id: payload._id, id: payload.id };
    if (payload.id) return null;
    const serialTaken =
      serial && meters.some(item => normalizeSearchField(item.serial) === serial && item.commodity === commodity);
    return serialTaken ? null : {};
  }

  if (payload._id && meter._id !== payload._id) return null;
  if (payload.id && Number(meter.toolsId) !== payload.id) return null;
  if (!meter._id) return null;
  if (!hasExplicitIds) {
    const meterSerial = normalizeSearchField(meter.serial);
    if (serial && meterSerial && serial !== meterSerial) return null;
    if (commodity && commodity !== meter.commodity) return null;
  }

  const id = meter.toolsId == null ? undefined : Number(meter.toolsId);
  if (id !== undefined && (!Number.isInteger(id) || id <= 0)) return null;
  return { _id: meter._id, id };
}
